import { useState } from "react";
import {
  Drawer,
  Button,
  Input,
  Table,
  Form,
  message,
  Typography,
  Tooltip,
  Row,
  Col,
  Tag,
  Image,
  Divider,
  Spin,
  Upload,
} from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { useQuery, useMutation } from "@apollo/client";
import {
  GET_PRODUCTS,
  CREATE_CATEGORY,
} from "./requests";
import { productTypes, categoryTypes } from "../../../../types";
import ProductList from "../../../../components/productsList";
import useUpload from "../../../../hooks/use-upload";

const { Text } = Typography;

interface CategoryDrawerProps {
  showModal: boolean;
  setShowModal: (value: boolean) => void;
  refetch: () => void;
  sortRefetch: () => void;
}

const CreateCategory = ({
  showModal,
  setShowModal,
  refetch,
  sortRefetch,
}: CategoryDrawerProps) => {
  const [form] = Form.useForm<typeof categoryTypes.category>();
  const { upload } = useUpload();
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const [searchTitle, setSearchTitle] = useState("");
  const [loading, setLoading] = useState(false);
  const [pageConfig, setPageConfig] = useState({ page: 1,
    pageSize: 10 });
  const [uploadLoding, setUploadLoading] = useState(false);
  const [imageUrl, setImageUrl] = useState(null);

  const [createCategory] = useMutation(CREATE_CATEGORY);

  const { data, loading: loadingProducts } = useQuery(GET_PRODUCTS, { variables: { params: pageConfig,
    name:searchTitle } });

  const products = data?.products.results || [];

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const values = await form.validateFields();

      const payload = {
        variables: {
          category: {
            ...values,
            img: imageUrl,
          },
          productCategories: selectedProducts,
        },
      }

      await createCategory(payload);
      await refetch();
      await sortRefetch();

      message.success("Categoría creada exitosamente");
      onClose();
    } catch (error) {
      console.log(error);
      message.error("Error al crear la categoría");
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async ({ fileList }) => {
    if (fileList[0]) {
      const fileName = `categories/${new Date().toISOString()}/${
        fileList[0]?.name
      }`;
      setUploadLoading(true);
      const url = await upload(fileList[0].originFileObj, fileName);

      setImageUrl(url);
    }
    setUploadLoading(false);
    message.success("Imagen agregada.");
  };

  const onClose = () => {
    setShowModal(false);
    setSearchTitle("");
    setSelectedProducts([]);
    form.resetFields();
  }

  const columns = [
    {
      title: "Nombre",
      key: "name",
      render: (row: typeof productTypes.product) => {
        return <Text strong>{row.name}</Text>;
      },
    },
    {
      title: "Descripción",
      key: "description",
      render: (row: typeof productTypes.product) => {
        return <Text strong>{row.description}</Text>;
      },
    },
    {
      title: 'Variantes',
      key: 'variants',
      dataIndex: 'variants',
      render: (variants: typeof productTypes.product['variants']) => {

        const lineItems = variants?.map((p) => ({
          product: {
            _id: p.id,
            img: p.img,
            title: p.title,
            description: p.description,
            product: {
              name: p.product.name,
              description: p.product.description,
            },
          },
        })) ?? [];

        if(lineItems.length > 0){
          return <ProductList lineItems={lineItems} />;
        } else {
          return <Tag color="yellow">Sin Productos</Tag>
        }
      },
    },
  ];

  const handleSelect = (record: typeof productTypes.product) => {
    const keys = [...selectedProducts];
    if (keys.includes(record.id)) {
      setSelectedProducts(keys.filter((key) => key !== record.id));
    } else {
      setSelectedProducts([...keys, record.id]);
    }
  };

  const rowSelection = {
    selectedRowKeys: selectedProducts,
    onChange: (selectedRowKeys: React.Key[]) => {
      setSelectedProducts(selectedRowKeys as string[]);
    },
    preserveSelectedRowKeys: true,
  };

  return (
    <Drawer
      title="Crear Nueva Categoría"
      open={showModal}
      onClose={onClose}
      width={"60%"}>
      <Form form={form} layout="vertical">
        <Form.Item
          name="name"
          label="Nombre"
          rules={[{ required: true }]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="description"
          label="Descripción"
          rules={[{ required: true }]}
        >
          <Input />
        </Form.Item>
      </Form>
      <Text>Imagen</Text>
      <Upload
        beforeUpload={() => false}
        listType="picture"
        maxCount={1}
        accept="image/*"
        onChange={(info) => {
          handleFileUpload(info);
        }}
        showUploadList={false}
      >
        <Button style={{ marginLeft: 10 }} icon={<UploadOutlined />}>Seleccionar Imagen</Button>
        {uploadLoding ? <Spin style={{ margin: 10 }} /> : null}
      </Upload>
      {imageUrl && (
        <Image style={{ marginLeft: 20 }} width={140} src={imageUrl} />
      )}
      <Divider />

      <Row>
        <Col span={18}>
          <Tooltip title="Buscar Producto por Nombre">
            <Input
              placeholder="Buscar Producto por Nombre"
              value={searchTitle}
              onChange={(e) => setSearchTitle(e.target.value)}
              allowClear
            />
          </Tooltip>
        </Col>
        <Col span={6}>
          <Text style={{ marginLeft:10,
            fontSize:"15px" }} strong>Productos Seleccionados: {selectedProducts.length}</Text>
        </Col>
      </Row>
      <div style={{ marginTop: 10 }}>
        <Table<typeof productTypes.product>
          dataSource={products}
          rowKey="id"
          loading={loadingProducts}
          pagination={{
            current: Number(pageConfig.page),
            defaultCurrent: pageConfig.page,
            pageSize: Number(pageConfig.pageSize),
            defaultPageSize: pageConfig.pageSize,
            total: data?.products?.info?.count,
            showTotal: (total) => (
              <Text disabled>{`${total} resultados`}</Text>
            ),
            onChange: (page, pageSize) => {
              setPageConfig({
                page,
                pageSize,
              });
            },
            showSizeChanger: true,
          }}
          rowSelection={rowSelection}
          onRow={(record) => ({
            onClick: () => handleSelect(record),
          })}
          columns={columns}
        />
      </div>
      <Button
        size="large"
        loading={loading}
        type="primary"
        onClick={handleSubmit}
        style={{ marginTop: 20 }}>
        Crear Categoría
      </Button>
    </Drawer>
  );
};

export default CreateCategory;
